/* eslint complexity: ["error", {max: 10}] -- many conditionals on whether or not to redirect elsewhere */
/* eslint max-statements: ["error", {max: 24}] -- many conditionals on whether or not to redirect elsewhere */

import { useCallback, useEffect, useMemo, useRef } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import noop from "lodash/noop";

import { LoginAnalyticsEvent } from "@/constants/analytics/Login";
import { LogoutReason } from "@/constants/Login";
import {
  DefaultStackScreenOptions,
  OnboardingScreenPath,
} from "@/constants/Navigation";
import { router, Stack } from "@/crossPlatform/expo-router";
import useAuth from "@/hooks/useAuth";
import useWaitingRoomIntercept from "@/hooks/useWaitingRoomIntercept";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetBasicInfoQuery, useGetFullProfileQuery } from "@/store/api/user";
import { useAppSelector } from "@/store/hooks";
import { useAppStartupDispatch } from "@/store/reducers/appStartup";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  useCanPostLoginRoutingProceedSelector,
  useLogoutReasonSelector,
} from "@/store/reducers/auth";
import { selectPatientId } from "@/store/reducers/booking";
import { getIsOnboardedOnMobile } from "@/utils/user.util";

const Layout = () => {
  const { track } = useAnalytics();
  const { captureMessage } = useAppMonitor();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();
  const { externalId: appUserId } = useAppUserSelector();
  const { isAuthenticated } = useAuth();
  const patientId = useAppSelector(selectPatientId);
  const { deleteWaitingRoomReservation } = useWaitingRoomIntercept();
  const hasDeletedReservation = useRef<boolean>(false);
  const logoutReason = useLogoutReasonSelector();
  // this boolean exists because there are situations where we want to display a no-op, no-side-effect, splash screen
  // *before* proceeding to postLoginRouting fully routing. one better option would be to have a separate screen like
  // app/(loading)/index.tsx that is just a splsh screen, with an otherwise empty app/(loading)/_layout.tsx.
  // however, since presently there is no way to disable animations on routing properly, this would lead to a double splash screen
  // as the first splash screen is routed to (app/(loading)/index.tsx), and then later this splash screen (app/(postLoginRouting)/index), then the app content
  const canPostLoginRoutingProceed = useCanPostLoginRoutingProceedSelector();
  const shouldSkipQueries = !canPostLoginRoutingProceed || !isAuthenticated;
  const {
    data: basicInfoData,
    error: basicInfoError,
    isError: isBasicInfoError,
    isFetching: isBasicInfoFetching,
    isLoading: isBasicInfoLoading,
  } = useGetBasicInfoQuery(undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    skip: shouldSkipQueries,
  });
  const {
    data: fullProfileData,
    error: fullProfileError,
    isError: isFullProfileError,
    isFetching: isFullProfileFetching,
    isLoading: isFullProfileLoading,
  } = useGetFullProfileQuery(undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    skip: shouldSkipQueries,
  });
  const isDataFetching = isBasicInfoFetching || isFullProfileFetching;
  const isDataLoading = isBasicInfoLoading || isFullProfileLoading;
  const isDataReady =
    !isDataFetching && !isDataLoading && canPostLoginRoutingProceed;

  useEffect(() => {
    // not performing actual routing if this is just for the splash screen; this is fine
    if (!canPostLoginRoutingProceed) return;
    if (isAuthenticated) return;
    // if a repo refactor ever moves the (tabs) directory or the TabLayout component
    // to be anywhere, even recursively, under this file's directory (ie: (postLoginRouting))
    // then this error will happen on every app launch, see the comment in 'app/(app)/_layout.tsx'
    // about expo immediately recursively finding and loading TabLayout/(tabs) on app start, before any app initialization even begins
    captureMessage(
      "Impossible scenario reached: LandingPage has been routed to (and not in SplashScreenOnly mode), but user is not authenticated, something has gone very wrong",
    );
  }, [canPostLoginRoutingProceed, captureMessage, isAuthenticated]);

  useEffect(() => {
    // wait for patientId to be populated in Redux before calling — it may be null early in the login flow
    if (
      !canPostLoginRoutingProceed ||
      !patientId ||
      hasDeletedReservation.current
    ) {
      return;
    }
    hasDeletedReservation.current = true;
    deleteWaitingRoomReservation().catch(noop);
  }, [patientId, canPostLoginRoutingProceed, deleteWaitingRoomReservation]);

  // Track if we've already redirected to prevent multiple redirects
  const hasRedirected = useRef<boolean>(false);

  const isReadyForNavigation = useCallback(() => {
    if (hasRedirected.current || !isAuthenticated || !isDataReady) {
      return false;
    }

    if (isBasicInfoError || !isNil(basicInfoError)) {
      throw basicInfoError;
    }
    if (isFullProfileError || !isNil(fullProfileError)) {
      throw fullProfileError;
    }
    if (isNil(basicInfoData)) {
      throw new Error("Fetched user's info from API but received empty data");
    }
    if (isNil(fullProfileData)) {
      throw new Error(
        "Fetched user's full profile info from API but received empty data",
      );
    }
    // getBasicInfo updates the AppUser redux store on success,
    // we wait for getBasicInfo to complete, AND this redux change to be propagated
    // as components on the postLoginRouting page / home tab reasonably expect this
    // minimal appUser redux store to be populated
    return !isEmpty(appUserId);
  }, [
    appUserId,
    basicInfoData,
    basicInfoError,
    fullProfileData,
    fullProfileError,
    isAuthenticated,
    isBasicInfoError,
    isDataReady,
    isFullProfileError,
  ]);

  const isUserOnboarded = useMemo(
    () => getIsOnboardedOnMobile(basicInfoData?.onboardings),
    [basicInfoData?.onboardings],
  );

  const handleNavigation = useCallback(() => {
    // Don't set canLoggedInUserSeeAppContent during an active logout flow
    // When user logs out, we set canLoggedInUserSeeAppContent(false) to hide app content,
    // but since credentials aren't cleared yet, protected routes might briefly render
    // PostLoginRouting. We shouldn't undo the logout flag setting in that case.
    const isLoggingOut = logoutReason !== LogoutReason.JustPreLoginCleanup;
    if (isLoggingOut) {
      return;
    }

    track(LoginAnalyticsEvent.SuccessfulLogin);
    hasRedirected.current = true;

    if (!isUserOnboarded) {
      router.replace(OnboardingScreenPath.VerifyInformation);
    }
    // this file, app/(postLoginRouting)/_layout.tsx, should be the one and ONLY pathway into ScreenPath.App
    // and the checks here need to happen and need to be verified before routing to the App
    setCanLoggedInUserSeeAppContent(true);
  }, [isUserOnboarded, logoutReason, setCanLoggedInUserSeeAppContent, track]);

  useEffect(() => {
    // do not perform actual routing if this is just for the splash screen
    if (!canPostLoginRoutingProceed) return;
    // if not yet ready for navigation, we will stay on this SplashScreen (ie: app/(postLoginRouting)/index.tsx)
    if (!isReadyForNavigation()) return;
    handleNavigation();
  }, [canPostLoginRoutingProceed, handleNavigation, isReadyForNavigation]);

  return (
    <Stack>
      <Stack.Screen name="index" options={DefaultStackScreenOptions} />
    </Stack>
  );
};

export default Layout;
